import { useEffect, useMemo, useRef } from "react";
import { useReportFiltersStore } from "../../../store/reportFilters";

export type FilterOption = { value: string; label: string };

const byLabel = (a: FilterOption, b: FilterOption) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: "base" });

/**
 * Location dropdown options for a report: the event's active locations that
 * ReportFilters loads for the Vendor dropdown. Report rows are already filtered
 * by location, so options derived from the rows shrink to the picked location
 * and block a second pick in the multi-select.
 */
export const useLocationFilterOptions = (): FilterOption[] => {
  const allLocations = useReportFiltersStore((state) => state.allLocations);
  return useMemo(() => {
    const map = new Map<string, string>(); // value -> label
    for (const loc of allLocations) {
      if (loc?.id == null) continue;
      const value = String(loc.id);
      if (!map.has(value)) map.set(value, loc.name || value);
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort(byLabel);
  }, [allLocations]);
};

/**
 * Options derived from report rows that survive later, narrower loads. Every
 * value seen since `resetKey` last changed stays in the list, so a multi-select
 * fed by the rows (usernames) can still offer the values the current filter
 * excluded. Pass the event/organization scope as `resetKey` so values from one
 * event do not leak into another.
 */
export const useAccumulatedOptions = <T,>(
  rows: T[],
  getValue: (row: T) => string | null | undefined,
  resetKey: string
): FilterOption[] => {
  const seenRef = useRef<{ key: string; values: Set<string> }>({ key: resetKey, values: new Set() });
  const getValueRef = useRef(getValue);
  useEffect(() => {
    getValueRef.current = getValue;
  });

  return useMemo(() => {
    if (seenRef.current.key !== resetKey) {
      seenRef.current = { key: resetKey, values: new Set() };
    }
    const seen = seenRef.current.values;
    for (const row of rows) {
      const value = getValueRef.current(row);
      if (value) seen.add(value);
    }
    return Array.from(seen)
      .map((value) => ({ value, label: value }))
      .sort(byLabel);
  }, [rows, resetKey]);
};
